import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { errMsg } from '@/services/staffAttendanceApi';
import { jobberMasterApi } from '@/services/jobberMasterApi';

const EMPTY = { name: '', legal_name: '', gstin: '', address1: '', address2: '', place: '', pincode: '', state: '', contact_person: '', phone: '', categories: [], active: true };
const KEYS = Object.keys(EMPTY);
const DEFAULT_CATS = ['Processing Unit', 'Flexi Stitching Unit', 'Shirt Stitching Unit', 'Folding Unit'];
const fromJobber = (j, k) => {
  if (k === 'active') return !!j.active;
  if (k === 'categories') return j.categories || [];
  return k === 'gstin' && j.gstin === 'URP' ? '' : j[k] || '';
};

const CategoryField = ({ f, set, categories }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold text-gray-700">Categories</Label>
    <div className="flex flex-wrap gap-2" data-testid="jobber-form-categories">
      {categories.map((c) => {
        const on = f.categories.includes(c);
        return (
          <button key={c} type="button" onClick={() => set({ categories: on ? f.categories.filter((x) => x !== c) : [...f.categories, c] })}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${on ? 'border-sky-600 bg-sky-600 text-white' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
            data-testid={`jobber-form-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} aria-pressed={on}>{c}</button>
        );
      })}
    </div>
    <p className="text-[11px] text-gray-500">Job orders sent to a category list only the jobbers tagged with it.</p>
  </div>
);

const Field = ({ label, k, f, set, req, ...rest }) => (
  <div className="space-y-1">
    <Label className="text-xs font-semibold text-gray-700">{label}{req && <span className="text-rose-600"> *</span>}</Label>
    <Input value={f[k]} onChange={(e) => set({ [k]: e.target.value })} data-testid={`jobber-form-${k}`} {...rest} />
  </div>
);

const StateField = ({ f, set, states }) => {
  const g = f.gstin.trim().toUpperCase();
  const fromGst = g && g !== 'URP' ? states.find((s) => s.code === g.slice(0, 2))?.name : null;
  return (
    <div className="space-y-1">
      <Label className="text-xs font-semibold text-gray-700">State{!g && <span className="text-rose-600"> *</span>}</Label>
      {g && g !== 'URP' ? (
        <div className="h-10 flex items-center rounded-md border bg-gray-50 px-3 text-sm text-gray-700" data-testid="jobber-form-state-auto">
          {fromGst || 'Taken from the GSTIN'}<span className="ml-auto text-[11px] text-gray-400">from GSTIN</span>
        </div>
      ) : (
        <Select value={f.state || undefined} onValueChange={(v) => set({ state: v })}>
          <SelectTrigger data-testid="jobber-form-state"><SelectValue placeholder="Pick state" /></SelectTrigger>
          <SelectContent className="max-h-72">{states.map((s) => <SelectItem key={s.code} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
      )}
    </div>
  );
};

/** Add / edit one jobber. `jobber` null = new (optionally `initialName`), object = edit. */
export const JobberFormDialog = ({ open, jobber, initialName = '', states, categories, onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [stateList, setStateList] = useState(states || []);
  useEffect(() => {
    if (!open) return;
    setErr('');
    setF(jobber ? Object.fromEntries(KEYS.map((k) => [k, fromJobber(jobber, k)])) : { ...EMPTY, name: initialName });
    if (!states?.length && !stateList.length) jobberMasterApi.states().then(setStateList).catch(() => {});
  }, [open, jobber, initialName]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const list = states?.length ? states : stateList;
  const ready = f.name.trim() && f.address1.trim() && f.place.trim() && f.pincode.trim();

  const save = async () => {
    setBusy(true); setErr('');
    const g = f.gstin.trim().toUpperCase();
    const body = g && g !== 'URP' ? { ...f, state: '' } : f;
    try {
      const r = jobber ? await jobberMasterApi.update(jobber.id, body) : await jobberMasterApi.create(body);
      toast.success(jobber ? `Saved ${r.name}` : `Added ${r.name}`);
      onSaved(r);
    } catch (e) { setErr(errMsg(e, 'Save failed')); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="jobber-form-dialog">
        <DialogHeader>
          <DialogTitle>{jobber ? `Edit ${jobber.name}` : 'Add jobber'}</DialogTitle>
          <DialogDescription>Job-work e-way bills take the GSTIN, address, place, PIN and state from here.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Jobber name (shown in WMS)" k="name" f={f} set={set} req autoFocus />
          <Field label="Legal name (on the e-way bill)" k="legal_name" f={f} set={set} placeholder="Blank = same as jobber name" />
          <Field label="GSTIN" k="gstin" f={f} set={set} placeholder="Blank = Unregistered" maxLength={15} className="uppercase font-mono" />
          <StateField f={f} set={set} states={list} />
          <Field label="Address line 1" k="address1" f={f} set={set} req maxLength={120} />
          <Field label="Address line 2" k="address2" f={f} set={set} maxLength={120} />
          <Field label="Place" k="place" f={f} set={set} req maxLength={50} />
          <Field label="PIN" k="pincode" f={f} set={set} req inputMode="numeric" maxLength={6} className="font-mono" />
          <Field label="Contact person" k="contact_person" f={f} set={set} />
          <Field label="Phone" k="phone" f={f} set={set} inputMode="tel" placeholder="10-digit number" />
        </div>
        <CategoryField f={f} set={set} categories={categories?.length ? categories : DEFAULT_CATS} />
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => set({ active: v })} data-testid="jobber-form-active" />Active — shows in job-order jobber lists</label>
        {err && <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" data-testid="jobber-form-error">{err}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="jobber-form-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !ready} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="jobber-form-save">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{jobber ? 'Save' : 'Add jobber'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default JobberFormDialog;
